import TrainingKPIDestroy from "issara-sdk/apis/TrainingKPIDestroy_apps_HRM";
import TrainingKPIListCreate from "issara-sdk/apis/TrainingKPIListCreate_apps_HRM";
import EmployeeList from "issara-sdk/apis/EmployeeList_users";

import TrainingKPISerializer from "issara-sdk/types/TrainingKPISerializer_apps_HRM";

import {
  ActionHandler,
  BaseSeqState,
  BLC_STATUS,
  GenerateActionTypes,
  generatePrefixedActions,
  MasterMapOptions,
  PickMainState,
  SetErrorMessage,
} from "react-lib/apps/HISV3/common/CommonInterface";

/** ============== Type-Defining Constants =============  */
export const ACTIONS = {
  ADD_EMPLOYEE: "ADD_EMPLOYEE",
  REMOVE: "REMOVE",
  SAVE: "SAVE",
  SEARCH: "SEARCH",
} as const;

export const SEQUENCE_NAME = "TrainingKPISetting" as const;

/** =============== Types and Interfaces ===============  */
export type State = Partial<{
  TrainingKPISettingSequence: Partial<{
    sequenceIndex: "Action" | "START" | null;
    filter: FilterType;
    items: TrainingKPIType[];
  }> | null;
  masterOptions: MasterOptions;
}>;

export type PickedState = PickMainState<
  "buttonLoadCheck" | "errorMessage" | "searchedItemListWithKey" | "selectedDivision"
>;

export type MasterOptions = MasterMapOptions<typeof Masters>;

type TrainingKPIType = Omit<TrainingKPISerializer, "employee_status"> & {
  employee_status: "ACTIVE" | "INACTIVE";
};

type FilterType = Partial<{
  division: number;
  employeeName: string;
  position: number;
  positionName: string;
}>;

export type Event =
  | { message: "GetMasterData"; params: Record<string, unknown> }
  | { message: "RunSequence"; params: Record<string, unknown> };

export type Data = {
  device?: number;
  division?: number;
};

export type SeqState = BaseSeqState<SequenceName>;

export type Actions = typeof ACTIONS;

type SequenceName = typeof SEQUENCE_NAME;

type ActionPayloadMap = {
  [ACTIONS.ADD_EMPLOYEE]: {
    data: Record<string, any>;
    onError?: (error?: string) => void;
    onSuccess: () => void;
  };
  [ACTIONS.REMOVE]: { data: TrainingKPISerializer; onSuccess: () => void };
  [ACTIONS.SEARCH]: { employeeName?: string; positionName?: string };
};

type ActionTypes = GenerateActionTypes<
  Actions,
  ActionPayloadMap,
  SequenceName,
  PickedState & State
>;

type ActionType = ActionTypes["ActionType"];

export type RunSequence = ActionTypes["RunSequence"];

export type SetProp = ActionTypes["SetProp"];

export type OnEvent = ActionTypes["OnEvent"];

type Params<A extends ActionType["action"]> = ActionTypes["Params"][A];

export type Handler<P = unknown, R = void> = ActionHandler<PickedState & State, Event, Data, P, R>;

/** ===================== Constants ====================  */
export const StateInitial: State = {
  TrainingKPISettingSequence: null,
};

export const DataInitial: Data = {};

const Masters = [
  ["positionAll", {}],
  ["division", {}],
] as const;

export const BTN_ACTS = generatePrefixedActions(ACTIONS, SEQUENCE_NAME);

/** ===================== Functions ====================  */
export const Start: Handler<SeqState> = async (controller, params) => {
  const state = controller.getState();

  controller.handleEvent({ message: "GetMasterData", params: { masters: Masters } });

  controller.setState(
    {
      TrainingKPISettingSequence: {
        ...state.TrainingKPISettingSequence,
        sequenceIndex: "Action",
        filter: { division: state.selectedDivision?.id },
      },
    },
    () => {
      Action(controller, { ...params, action: ACTIONS.SEARCH });
    }
  );
};

/** ====================================================  */
/**                        Action                         */
/** ====================================================  */
export const Action: Handler<ActionType> = async (controller, params) => {
  const actionHandlers: Partial<{ [K in ActionType["action"]]: Handler<Params<K>> }> = {
    [ACTIONS.ADD_EMPLOYEE]: HandleAddEmployee,
    [ACTIONS.REMOVE]: HandleRemove,
    [ACTIONS.SAVE]: HandleSave,
    [ACTIONS.SEARCH]: HandleSearch,
  };

  const { action } = params;

  const handler = actionHandlers[action] as Handler<Params<typeof action>> | undefined;

  if (handler) {
    handler(controller, params);
  } else {
    console.error(`No handler found for action: ${action}`);
  }
};

/** ====================================================  */
/**                        Handler                        */
/** ====================================================  */
const HandleSearch: Handler<Params<Actions["SEARCH"]>, Promise<void>> = async (
  controller,
  params
) => {
  const state = controller.getState();

  controller.setState({
    buttonLoadCheck: { ...state.buttonLoadCheck, [BTN_ACTS.SEARCH]: BLC_STATUS.LOADING },
  });

  const filter = state.TrainingKPISettingSequence?.filter ?? {};

  const [result] = await TrainingKPIListCreate.list({
    apiToken: controller.apiToken,
    params: { affiliation: filter.division },
  });

  controller.setState({
    TrainingKPISettingSequence: {
      ...state.TrainingKPISettingSequence,
      filter: { ...filter, employeeName: params.employeeName, positionName: params.positionName },
      items: result?.items || [],
    },
  });

  controller.setState({
    buttonLoadCheck: { ...state.buttonLoadCheck, [BTN_ACTS.SEARCH]: BLC_STATUS.SUCCESS },
  });
};

const HandleSave: Handler<Params<Actions["SAVE"]>> = async (controller, params) => {
  const state = controller.getState();

  controller.setState({
    buttonLoadCheck: { ...state.buttonLoadCheck, [BTN_ACTS.SAVE]: BLC_STATUS.LOADING },
  });

  const items = state.TrainingKPISettingSequence?.items ?? [];

  const formattedItems = items.map((item) => ({
    id: item.id,
    employee: item.employee,
    minimum_course_per_month: item.minimum_course_per_month,
    minimum_hour_per_month: item.minimum_hour_per_month,
    minimum_score_per_month: item.minimum_score_per_month,
  }));

  const [, error] = await TrainingKPIListCreate.create({
    apiToken: controller.apiToken,
    data: { items: formattedItems } as any,
  });

  if (error) {
    SetErrorMessage(controller, { ...params, error });

    return;
  }

  await HandleSearch(controller, {
    ...params,
    action: ACTIONS.SEARCH,
    ...state.TrainingKPISettingSequence?.filter,
  });

  controller.setState({
    buttonLoadCheck: { ...state.buttonLoadCheck, [BTN_ACTS.SAVE]: BLC_STATUS.SUCCESS },
  });
};

const HandleRemove: Handler<Params<Actions["REMOVE"]>> = async (controller, params) => {
  const state = controller.getState();

  controller.setState({
    buttonLoadCheck: { ...state.buttonLoadCheck, [BTN_ACTS.REMOVE]: BLC_STATUS.LOADING },
  });

  if (params.data.id) {
    const [, error] = await TrainingKPIDestroy.delete({
      apiToken: controller.apiToken,
      pk: params.data.id,
    });

    if (error) {
      SetErrorMessage(controller, { ...params, error });

      return;
    }
  }

  const items = state.TrainingKPISettingSequence?.items ?? [];

  const filteredItems = items.filter((item) => item.employee !== params.data.employee);

  controller.setState({
    TrainingKPISettingSequence: { ...state.TrainingKPISettingSequence, items: filteredItems },
    buttonLoadCheck: { ...state.buttonLoadCheck, [BTN_ACTS.REMOVE]: null },
  });

  params.onSuccess();
};

const HandleAddEmployee: Handler<Params<Actions["ADD_EMPLOYEE"]>> = async (controller, params) => {
  const state = controller.getState();

  controller.setState({
    buttonLoadCheck: { ...state.buttonLoadCheck, [BTN_ACTS.ADD_EMPLOYEE]: BLC_STATUS.LOADING },
  });

  const items = state.TrainingKPISettingSequence?.items ?? [];
  const filter = { division: state.TrainingKPISettingSequence?.filter?.division };

  if (params.data.type === "division" && params.data.value) {
    const divisionId = params.data.value;

    const [result, error] = await EmployeeList.list({
      apiToken: controller.apiToken,
      params: { limit: 1000, offset: 0, pagination_class: "page_number" },
    });

    if (error || !result?.items) {
      controller.setState({
        buttonLoadCheck: { ...state.buttonLoadCheck, [BTN_ACTS.ADD_EMPLOYEE]: null },
      });
      params.onError?.();
      return;
    }

    const employeesInDivision = result.items.filter(
      (emp: any) => Number(emp.affiliate) === Number(divisionId)
    );

    const existingEmployees = new Set(items.map((i) => i.employee_code || i.employee_name));
    const newEmployees = employeesInDivision.filter(
      (emp: any) =>
        !existingEmployees.has(emp.code) &&
        !existingEmployees.has(emp.full_name || `${emp.first_name_en} ${emp.last_name_en}`)
    );

    if (newEmployees.length === 0) {
      const employeesInDivisionExist = employeesInDivision.length > 0;

      if (employeesInDivisionExist) {
        controller.setState({
          buttonLoadCheck: { ...state.buttonLoadCheck, [BTN_ACTS.ADD_EMPLOYEE]: null },
        });
        params.onError?.("all-added");
      } else {
        controller.setState({
          buttonLoadCheck: { ...state.buttonLoadCheck, [BTN_ACTS.ADD_EMPLOYEE]: null },
        });
        params.onError?.("no-employees"); // no employee in division
      }
      return;
    }

    const employeeKPIs: TrainingKPIType[] = newEmployees.map((emp: any) => {
      const positionText =
        (state.masterOptions?.positionAll ?? []).find(
          (pos) => Number(pos.value) === Number(emp.position)
        )?.text || "";

      const affiliateText =
        (state.masterOptions?.division ?? []).find(
          (div) => Number(div.value) === Number(emp.affiliate)
        )?.text || "";

      return {
        employee: emp.id,
        employee_affiliation: affiliateText,
        employee_code: emp.code,
        employee_name: emp.full_name || `${emp.first_name_en} ${emp.last_name_en}`,
        employee_position: positionText,
        employee_status: emp.status === "ACTIVE" ? "ACTIVE" : "INACTIVE",
        minimum_course_per_month: "0",
        minimum_hour_per_month: "0",
        minimum_score_per_month: "0",
      };
    });

    controller.setState({
      TrainingKPISettingSequence: {
        ...(state.TrainingKPISettingSequence ?? {}),
        filter,
        items: [...items, ...employeeKPIs],
      },
      buttonLoadCheck: { ...state.buttonLoadCheck, [BTN_ACTS.ADD_EMPLOYEE]: BLC_STATUS.SUCCESS },
    });

    await TrainingKPIListCreate.create({
      apiToken: controller.apiToken,
      data: {
        items: employeeKPIs.map((emp) => ({
          employee: emp.employee,
          minimum_course_per_month: emp.minimum_course_per_month,
          minimum_hour_per_month: emp.minimum_hour_per_month,
          minimum_score_per_month: emp.minimum_score_per_month,
        })),
      } as any,
    });

    params.onSuccess?.();
    return;
  }

  const { data } = params;
  const foundPosition = (state.masterOptions?.positionAll ?? []).find(
    (option) => Number(option.value) === data.position
  );
  const foundDivision = (state.masterOptions?.division ?? []).find(
    (option) => Number(option.value) === data.affiliate
  );

  const hasMember = items.some((item) => item.employee === data.id);

  if (hasMember) {
    controller.setState({
      buttonLoadCheck: { ...state.buttonLoadCheck, [BTN_ACTS.ADD_EMPLOYEE]: null },
    });
    params.onError?.();
    return;
  }

  const employeeKPI: TrainingKPIType = {
    employee: data.id,
    employee_affiliation: foundDivision?.text || "",
    employee_code: data.code || "",
    employee_name: data.full_name,
    employee_position: foundPosition?.text || "",
    employee_status: data.status ? "ACTIVE" : "INACTIVE",
    minimum_course_per_month: "0",
    minimum_hour_per_month: "0",
    minimum_score_per_month: "0",
  };

  controller.setState({
    TrainingKPISettingSequence: {
      ...state.TrainingKPISettingSequence,
      filter,
      items: [...items, employeeKPI],
    },
    buttonLoadCheck: { ...state.buttonLoadCheck, [BTN_ACTS.ADD_EMPLOYEE]: BLC_STATUS.SUCCESS },
  });

  await TrainingKPIListCreate.create({
    apiToken: controller.apiToken,
    data: {
      items: [
        {
          employee: data.id,
          minimum_course_per_month: employeeKPI.minimum_course_per_month,
          minimum_hour_per_month: employeeKPI.minimum_hour_per_month,
          minimum_score_per_month: employeeKPI.minimum_score_per_month,
        },
      ],
    } as any,
  });

  params.onSuccess?.();
};
